import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../../utils/apiClient';

import PageContainer from '../layout/PageContainer';
// LINE管理専用のaxiosインスタンス（apiClientを使用）
const lineAxios = apiClient;

// クーポン管理用のaxiosインスタンス（apiClientを使用）
const couponAxios = apiClient;

import {
  Trophy, User, Calendar, TrendingUp, Clock, DollarSign,
  ChevronDown, ChevronUp, Award, Crown, Star, Sparkles,
  BarChart, Activity, CloudRain, Sun, Filter, Search,
  CheckSquare, Square, Send, Gift, MessageCircle, X,
  ChevronRight, Users, Ticket, Mail, AlertCircle, Settings,
  FileText, Check, Tag, Edit2, Plus, Trash2, Home,
  Ban, MailOpen, CheckCircle
} from 'lucide-react';
import CouponManagement from '../admin/CouponManagement';
import MessageTemplates from '../admin/MessageTemplates';
import MessageSender from './MessageSender';
import MemberProfileSettings from './MemberProfileSettings';

interface LineMember {
  id: number;
  line_id: string;
  display_name: string;
  name?: string;
  points: number;
  total_points_earned: number;
  stamps?: number;
  total_purchase?: number;
  total_purchases?: number;
  visits_count?: number;
  visit_count?: number;
  last_visit?: string;
  created_at: string;
  current_rank?: string;
  email?: string;
  labels?: Label[];
  memo?: string;
  is_blocked?: number;
  blocked_at?: string;
  unblocked_at?: string;
  latest_message_sent?: string;
  latest_message_read?: number;
  latest_message_read_at?: string;
}

interface Label {
  id: number;
  name: string;
  color: string;
  label_name?: string; // 後方互換性のため
}

interface MemberExtension {
  line_id: string;
  labels: { id: number; name: string; color: string }[];
  memo: string | null;
}

interface MemberStats {
  total_members: number;
  total_points: number;
  total_stamps: number;
  avg_purchase_amount: number;
  avg_visit_count: number;
  active_members_30days: number;
}

interface VisitPattern {
  weekday: { [key: string]: number };
  timeSlot: { [key: string]: number };
  monthly: { month: number; visits: number }[];
  weather?: { sunny: number; rainy: number; cloudy: number };
}

interface RankSetting {
  id: number;
  rank_name: string;
  min_points: number;
  max_points?: number | null;
  color: string;
  icon: string;
  benefits?: string;
  sort_order: number;
  is_active: boolean;
}

interface PointSettings {
  points_per_yen: number;
  point_expiry_days: number;
  ranks: RankSetting[];
}

interface FilterConditions {
  rankFilter: number[]; // ランクIDで管理
  pointRange: { min: number; max: number };
  visitRange: { min: number; max: number };
  purchaseRange: { min: number; max: number };
  lastVisitDays: number | null;
  memberSince: number | null;
}

interface BulkAction {
  type: 'coupon' | 'message' | 'points';
  title: string;
  content: string;
  pointsAmount?: number;
  expiryDays?: number;
}

const LineMemberManagementDynamic: React.FC = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<LineMember[]>([]);
  const [stats, setStats] = useState<MemberStats | null>(null);
  const [ranking, setRanking] = useState<LineMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<LineMember | null>(null);
  const [showLabelModal, setShowLabelModal] = useState<LineMember | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'points' | 'visits' | 'purchase'>('recent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [showVisitPattern, setShowVisitPattern] = useState(false);
  const [visitPattern, setVisitPattern] = useState<VisitPattern | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'pattern' | 'mypage'>('overview');
  
  // 新機能の状態
  const [selectedMembers, setSelectedMembers] = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showMessageSender, setShowMessageSender] = useState(false);
  const [bulkActionModal, setBulkActionModal] = useState<{ isOpen: boolean; type: 'coupon' | 'message' | 'points' | null }>({
    isOpen: false,
    type: null
  });
  
  // 動的ランク設定
  const [pointSettings, setPointSettings] = useState<PointSettings | null>(null);
  const [dynamicRanks, setDynamicRanks] = useState<RankSetting[]>([]);
  
  // 管理画面モーダル
  const [showManagementModal, setShowManagementModal] = useState<'coupon' | 'message' | 'campaign' | null>(null);
  
  // クーポン発行
  const [availableCoupons, setAvailableCoupons] = useState<any[]>([]);
  const [showCouponIssueModal, setShowCouponIssueModal] = useState(false);
  const [selectedCouponForIssue, setSelectedCouponForIssue] = useState<any>(null);
  const [issuedPins, setIssuedPins] = useState<{ memberName: string; pin: string; tier: string }[]>([]);
  const [showPinResultModal, setShowPinResultModal] = useState(false);
  const [skipExistingCheck, setSkipExistingCheck] = useState(false);
  const [couponIssueResults, setCouponIssueResults] = useState<{
    success: { name: string; lineId: string }[];
    skipped: { name: string; lineId: string; reason: string }[];
    failed: { name: string; lineId: string; reason: string }[];
  }>({ success: [], skipped: [], failed: [] });
  const [showIssueResultModal, setShowIssueResultModal] = useState(false);
  
  const [filters, setFilters] = useState<FilterConditions>({
    rankFilter: [],
    pointRange: { min: 0, max: 9999999 },
    visitRange: { min: 0, max: 999 },
    purchaseRange: { min: 0, max: 99999999 },
    lastVisitDays: null,
    memberSince: null
  });

  const [pointAdjustment, setPointAdjustment] = useState({
    isOpen: false,
    member: null as LineMember | null,
    points: 0,
    operation: 'add' as 'add' | 'subtract' | 'set'
  });

  // 一括ポイント調整用の状態
  const [bulkPointAdjustment, setBulkPointAdjustment] = useState({
    points: 0,
    operation: 'add' as 'add' | 'subtract' | 'set'
  });

  // ラベル・メモ機能の状態
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [memberExtensions, setMemberExtensions] = useState<Map<string, MemberExtension>>(new Map());
  const [showLabelManagement, setShowLabelManagement] = useState(false);
  const [editingMemo, setEditingMemo] = useState<{ lineId: string; memo: string } | null>(null);
  const [memoSearchKeyword, setMemoSearchKeyword] = useState('');
  const [labelFilter, setLabelFilter] = useState<number | null>(null);
  const [showNewLabelModal, setShowNewLabelModal] = useState(false);
  const [newLabel, setNewLabel] = useState({ name: '', color: '#667eea' });

  useEffect(() => {
    fetchPointSettings();
    fetchStats();
    fetchRanking();
    fetchAvailableCoupons();
    fetchLabels();
    // 会員データを最初に取得してから拡張情報を取得
    fetchMembers().then(() => {
      fetchMemberExtensions();
    });
  }, []);

  // ポイント設定とランク設定を取得
  const fetchPointSettings = async () => {
    try {
      const response = await lineAxios.get('/point-settings/settings');
      
      if (response.data) {
        setPointSettings(response.data);
        // アクティブなランクのみをフィルター用に設定
        const activeRanks = response.data.ranks?.filter((r: RankSetting) => r.is_active) || [];
        // sort_orderでソート
        activeRanks.sort((a: RankSetting, b: RankSetting) => a.sort_order - b.sort_order);
        setDynamicRanks(activeRanks);
      }
    } catch (error) {
      console.error('ポイント設定取得エラー:', error);
      // エラー時はデフォルトランクを設定
      setDynamicRanks([
        { id: 1, rank_name: 'ブロンズ', min_points: 0, max_points: 99, color: '#fb923c', icon: '🥉', benefits: '', sort_order: 1, is_active: true },
        { id: 2, rank_name: 'シルバー', min_points: 100, max_points: 499, color: '#9ca3af', icon: '🥈', benefits: '', sort_order: 2, is_active: true },
        { id: 3, rank_name: 'ゴールド', min_points: 500, max_points: 999, color: '#facc15', icon: '🥇', benefits: '', sort_order: 3, is_active: true },
        { id: 4, rank_name: 'プラチナ', min_points: 1000, max_points: null, color: '#a855f7', icon: '💎', benefits: '', sort_order: 4, is_active: true }
      ]);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await lineAxios.get('/line/members');
      setMembers(response.data);
    } catch (error) {
      console.error('会員データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/line/stats');
      setStats(response.data);
    } catch (error) {
      console.error('統計データ取得エラー:', error);
    }
  };

  const fetchRanking = async () => {
    try {
      const response = await lineAxios.get('/line/members?sort_by=points&limit=5');
      setRanking(response.data);
    } catch (error) {
      console.error('ランキング取得エラー:', error);
    }
  };

  const fetchVisitPattern = async (memberId: number) => {
    try {
      const dummyPattern: VisitPattern = {
        weekday: { 
          '月': Math.floor(Math.random() * 10) + 1,
          '火': Math.floor(Math.random() * 10) + 1,
          '水': Math.floor(Math.random() * 10) + 1,
          '木': Math.floor(Math.random() * 10) + 1,
          '金': Math.floor(Math.random() * 10) + 1,
          '土': Math.floor(Math.random() * 10) + 1,
          '日': Math.floor(Math.random() * 10) + 1
        },
        timeSlot: { 
          '朝(6-11)': Math.floor(Math.random() * 10) + 1,
          '昼(11-14)': Math.floor(Math.random() * 10) + 5,
          '夕(14-17)': Math.floor(Math.random() * 10) + 1,
          '夜(17-21)': Math.floor(Math.random() * 10) + 3
        },
        monthly: Array.from({length: 12}, (_, i) => ({ 
          month: i + 1, 
          visits: Math.floor(Math.random() * 15) 
        })),
        weather: {
          sunny: Math.floor(Math.random() * 50) + 20,
          rainy: Math.floor(Math.random() * 30) + 10,
          cloudy: Math.floor(Math.random() * 20) + 5
        }
      };
      setVisitPattern(dummyPattern);
      setShowVisitPattern(true);
    } catch (error) {
      console.error('来店パターン取得エラー:', error);
    }
  };

  // 動的ランク情報の取得
  const getDynamicRankInfo = (points: number): { rank: RankSetting | null; nextRank: RankSetting | null; pointsToNext: number } => {
    if (!dynamicRanks || dynamicRanks.length === 0) {
      return { rank: null, nextRank: null, pointsToNext: 0 };
    }

    // 現在のランクを判定
    let currentRank: RankSetting | null = null;
    let nextRank: RankSetting | null = null;

    for (let i = dynamicRanks.length - 1; i >= 0; i--) {
      const rank = dynamicRanks[i];
      if (points >= rank.min_points) {
        // max_pointsがnullまたは未定義の場合は最高ランク
        if (!rank.max_points || points <= rank.max_points) {
          currentRank = rank;
          // 次のランクを探す
          if (i < dynamicRanks.length - 1) {
            nextRank = dynamicRanks[i + 1];
          }
          break;
        }
      }
    }

    // 最低ランクにも満たない場合
    if (!currentRank && dynamicRanks.length > 0) {
      currentRank = dynamicRanks[0];
      if (dynamicRanks.length > 1) {
        nextRank = dynamicRanks[1];
      }
    }

    // 次のランクまでのポイント数を計算
    const pointsToNext = nextRank ? Math.max(0, nextRank.min_points - points) : 0;

    return { rank: currentRank, nextRank, pointsToNext };
  };

  // ランクカラーのスタイルを生成
  const getRankStyle = (color: string, isBackground: boolean = false) => {
    // カラーコードをそのまま使用
    if (color.startsWith('#')) {
      if (isBackground) {
        return { backgroundColor: color + '20', color: color }; // 背景は透明度を追加
      }
      return { color: color };
    }
    // Tailwindクラス名の場合（後方互換性）
    return isBackground ? `bg-${color}-50 text-${color}-600` : `text-${color}-600`;
  };

  // フィルタリング機能
  const applyFilters = (member: LineMember): boolean => {
    // ランクフィルター（ランクIDで比較）
    if (filters.rankFilter.length > 0) {
      const memberRankInfo = getDynamicRankInfo(member.total_points_earned || 0);
      if (!memberRankInfo.rank || !filters.rankFilter.includes(memberRankInfo.rank.id)) {
        return false;
      }
    }

    // ポイント範囲
    const points = member.points || 0;
    if (points < filters.pointRange.min || points > filters.pointRange.max) {
      return false;
    }

    // 来店回数範囲
    const visits = (member.visits_count || member.visit_count) || 0;
    if (visits < filters.visitRange.min || visits > filters.visitRange.max) {
      return false;
    }

    // 累計購入額範囲
    const purchase = (member.total_purchase || member.total_purchases) || 0;
    if (purchase < filters.purchaseRange.min || purchase > filters.purchaseRange.max) {
      return false;
    }

    // 最終来店日数
    if (filters.lastVisitDays !== null && member.last_visit) {
      const daysSinceLastVisit = Math.floor((Date.now() - new Date(member.last_visit).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceLastVisit > filters.lastVisitDays) {
        return false;
      }
    }

    // 会員歴
    if (filters.memberSince !== null) {
      const memberDays = Math.floor((Date.now() - new Date(member.created_at).getTime()) / (1000 * 60 * 60 * 24));
      if (memberDays < filters.memberSince) {
        return false;
      }
    }

    return true;
  };

  // ソート＆フィルター適用
  const filteredAndSortedMembers = React.useMemo(() => {
    let filtered = members.filter(applyFilters);
    
    // 検索フィルター
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(member => 
        member.display_name?.toLowerCase().includes(searchLower) ||
        member.line_id?.toLowerCase().includes(searchLower) ||
        member.email?.toLowerCase().includes(searchLower)
      );
    }

    // メモ検索フィルター
    if (memoSearchKeyword) {
      const searchLower = memoSearchKeyword.toLowerCase();
      filtered = filtered.filter(member => {
        const memo = memberExtensions.get(member.line_id)?.memo;
        return memo && memo.toLowerCase().includes(searchLower);
      });
    }

    // ラベルフィルター
    if (labelFilter) {
      filtered = filtered.filter(member => {
        const labels = memberExtensions.get(member.line_id)?.labels;
        return labels && labels.some(label => 
          allLabels.find(l => l.id === labelFilter && l.label_name === label.name)
        );
      });
    }

    // ソート
    filtered.sort((a, b) => {
      let compareValue = 0;
      switch (sortBy) {
        case 'points':
          compareValue = (a.points || 0) - (b.points || 0);
          break;
        case 'visits':
          compareValue = ((a.visits_count || a.visit_count) || 0) - ((b.visits_count || b.visit_count) || 0);
          break;
        case 'purchase':
          compareValue = ((a.total_purchase || a.total_purchases) || 0) - ((b.total_purchase || b.total_purchases) || 0);
          break;
        case 'recent':
        default:
          compareValue = new Date(a.last_visit || 0).getTime() - new Date(b.last_visit || 0).getTime();
          break;
      }
      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  }, [members, sortBy, sortOrder, searchTerm, filters, memoSearchKeyword, labelFilter, memberExtensions, allLabels]);

  // 選択機能
  const handleSelectAll = () => {
    if (selectedMembers.size === filteredAndSortedMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredAndSortedMembers.map(m => m.id)));
    }
  };

  const handleSelectMember = (memberId: number) => {
    const newSelection = new Set(selectedMembers);
    if (newSelection.has(memberId)) {
      newSelection.delete(memberId);
    } else {
      newSelection.add(memberId);
    }
    setSelectedMembers(newSelection);
  };

  // クーポン取得
  const fetchAvailableCoupons = async () => {
    try {
      const response = await lineAxios.get('/coupons/templates');
      if (response.data.success && response.data.templates) {
        setAvailableCoupons(response.data.templates.filter((c: any) => c.active));
      }
    } catch (error) {
      console.error('クーポン取得エラー:', error);
    }
  };

  // ラベル一覧取得
  const fetchLabels = async () => {
    try {
      const response = await lineAxios.get('/member-labels/labels');
      if (response.data.success) {
        setAllLabels(response.data.labels);
      }
    } catch (error) {
      console.error('ラベル取得エラー:', error);
    }
  };

  // 会員拡張情報取得
  const fetchMemberExtensions = async () => {
    try {
      const response = await lineAxios.get('/member-labels/extensions');
      if (response.data.success) {
        const extensionsMap = new Map<string, MemberExtension>();
        response.data.extensions.forEach((ext: any) => {
          // line_user_idをキーとして使用（APIはline_user_idを返す）
          const key = ext.line_user_id || ext.line_id;
          extensionsMap.set(key, {
            line_id: key,
            labels: ext.labels || [],
            memo: ext.memo
          });
        });
        // 新しいMapインスタンスを作成してReactの再レンダリングをトリガー
        setMemberExtensions(new Map(extensionsMap));
        console.log('拡張情報取得完了:', extensionsMap.size, '件');

        // デバッグ用：最初の会員の拡張情報を確認
        if (members.length > 0) {
          console.log('会員データあり。最初の会員:', members[0].line_id);
          console.log('その拡張情報:', extensionsMap.get(members[0].line_id));
        }
      }
    } catch (error) {
      console.error('会員拡張情報取得エラー:', error);
    }
  };

  // ラベル作成
  const createLabel = async () => {
    if (!newLabel.name) return;
    
    try {
      const response = await lineAxios.post('/member-labels/labels', {
        label_name: newLabel.name,
        color: newLabel.color
      });
      
      if (response.data.success) {
        await fetchLabels();
        setShowNewLabelModal(false);
        setNewLabel({ name: '', color: '#667eea' });
      }
    } catch (error) {
      console.error('ラベル作成エラー:', error);
    }
  };

  // ラベル削除
  const deleteLabel = async (labelId: number) => {
    if (!confirm('このラベルを削除してもよろしいですか？')) return;
    
    try {
      const response = await lineAxios.delete(`/member-labels/labels/${labelId}`);
      if (response.data.success) {
        await fetchLabels();
        await fetchMemberExtensions();
      }
    } catch (error) {
      console.error('ラベル削除エラー:', error);
    }
  };

  // 会員にラベル割り当て
  const assignLabel = async (lineId: string, labelId: number) => {
    console.log('assignLabel called:', { lineId, labelId });
    try {
      const response = await lineAxios.post(`/member-labels/members/${lineId}/labels`, {
        label_id: labelId
      });

      console.log('assignLabel response:', response.data);
      if (response.data.success) {
        await fetchMemberExtensions();
      }
    } catch (error) {
      console.error('ラベル割り当てエラー:', error);
    }
  };

  // 会員からラベル削除
  const removeLabel = async (lineId: string, labelId: number) => {
    try {
      const response = await lineAxios.delete(`/member-labels/members/${lineId}/labels/${labelId}`);
      if (response.data.success) {
        await fetchMemberExtensions();
      }
    } catch (error) {
      console.error('ラベル削除エラー:', error);
    }
  };

  // メモ保存
  const saveMemo = async (lineId: string, memo: string) => {
    try {
      const response = await lineAxios.put(`/member-labels/members/${lineId}/memo`, {
        memo
      });
      
      if (response.data.success) {
        await fetchMemberExtensions();
        setEditingMemo(null);
      }
    } catch (error) {
      console.error('メモ保存エラー:', error);
    }
  };

  // メモで検索
  const searchByMemo = async () => {
    if (!memoSearchKeyword) {
      fetchMembers();
      return;
    }
    
    try {
      const response = await lineAxios.get(`/api/member-labels/search/memo?keyword=${encodeURIComponent(memoSearchKeyword)}`);
      if (response.data.success) {
        setMembers(response.data.members);
      }
    } catch (error) {
      console.error('メモ検索エラー:', error);
    }
  };

  // ラベルで検索  
  const searchByLabel = async (labelId: number) => {
    setLabelFilter(labelId);
    try {
      const response = await lineAxios.get(`/api/member-labels/search/label?label_id=${labelId}`);
      if (response.data.success) {
        setMembers(response.data.members);
      }
    } catch (error) {
      console.error('ラベル検索エラー:', error);
    }
  };

  // クーポン発行処理
  const handleIssueCoupon = async () => {
    if (!selectedCouponForIssue || selectedMembers.size === 0) return;

    try {
      const selectedMembersList = members.filter(m => selectedMembers.has(m.id));
      const results = {
        success: [] as { name: string; lineId: string }[],
        skipped: [] as { name: string; lineId: string; reason: string }[],
        failed: [] as { name: string; lineId: string; reason: string }[]
      };
      const pinResults: { memberName: string; pin: string; tier: string }[] = [];

      for (const member of selectedMembersList) {
        const memberName = member.display_name || member.name || '名前未設定';
        const lineId = member.line_id;

        console.log(`発行中: ${memberName} (LINE ID: ${lineId})`);

        if (!lineId) {
          console.error(`LINE IDが見つかりません: ${memberName}`);
          results.failed.push({
            name: memberName,
            lineId: lineId || '不明',
            reason: 'LINE IDが見つかりません'
          });
          continue;
        }

        try {
          const response = await couponAxios.post('/coupons/issue', {
            templateId: selectedCouponForIssue.id,
            lineUserId: lineId,
            skipExistingCheck: skipExistingCheck
          });

          if (response.data.success) {
            // 成功
            results.success.push({ name: memberName, lineId });

            if (response.data.personal_pin) {
              pinResults.push({
                memberName,
                pin: response.data.personal_pin,
                tier: response.data.pin_tier || 'スタンダード'
              });
            }
          } else if (response.data.skipped) {
            // スキップ（既に発行済み）
            results.skipped.push({
              name: memberName,
              lineId,
              reason: response.data.message || 'クーポン既に発行済み'
            });
          } else {
            // 失敗
            results.failed.push({
              name: memberName,
              lineId,
              reason: response.data.message || '発行失敗'
            });
          }
        } catch (err: any) {
          console.error(`発行エラー (${memberName}):`, err);
          const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || '不明なエラー';
          results.failed.push({
            name: memberName,
            lineId,
            reason: errorMessage
          });
        }
      }

      setShowCouponIssueModal(false);
      setSelectedCouponForIssue(null);
      setSkipExistingCheck(false);

      // 結果を保存してモーダルを表示
      setCouponIssueResults(results);
      setShowIssueResultModal(true);

      if (pinResults.length > 0) {
        setIssuedPins(pinResults);
        // PINモーダルは結果モーダルの後に表示
      }

      fetchAvailableCoupons(); // 再読み込み
    } catch (error) {
      console.error('クーポン発行エラー:', error);
      alert('クーポンの発行に失敗しました');
    }
  };

  // 一括アクション処理
  const handleBulkAction = async () => {
    if (selectedMembers.size === 0) {
      alert('会員を選択してください');
      return;
    }

    if (bulkActionModal.type !== 'points') {
      return;
    }

    try {
      const selectedMembersList = members.filter(m => selectedMembers.has(m.id));
      let successCount = 0;
      let failCount = 0;

      // 各会員に対してポイント操作を実行
      for (const member of selectedMembersList) {
        try {
          await lineAxios.put(`/line/members/${member.line_id}/points`, {
            points: bulkPointAdjustment.points,
            operation: bulkPointAdjustment.operation
          });
          successCount++;
        } catch (err) {
          console.error(`ポイント調整エラー (${member.display_name || member.name}):`, err);
          failCount++;
        }
      }

      // 結果表示
      const operationText = bulkPointAdjustment.operation === 'add' ? '追加' :
                            bulkPointAdjustment.operation === 'subtract' ? '減算' : '設定';
      alert(`${successCount}名にポイントを${operationText}しました${failCount > 0 ? `\n(${failCount}名は失敗)` : ''}`);

      // 会員リスト再取得
      await fetchMembers();

      setBulkActionModal({ isOpen: false, type: null });
      setSelectedMembers(new Set());
      setBulkPointAdjustment({ points: 0, operation: 'add' });
    } catch (error) {
      console.error('一括処理エラー:', error);
      alert('処理中にエラーが発生しました');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '未訪問';
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  const formatVisitDate = (dateString?: string) => {
    if (!dateString) return '未訪問';
    return new Date(dateString.replace(' ', 'T') + 'Z').toLocaleDateString('ja-JP', {
      timeZone: 'Asia/Tokyo'
    });
  };

  const formatCurrency = (amount?: number) => {
    return `¥${(amount || 0).toLocaleString()}`;
  };

  const handleMemberSelect = (member: LineMember) => {
    setSelectedMember(member);
    fetchVisitPattern(member.id);
  };

  const handlePointAdjustment = async () => {
    if (!pointAdjustment.member) return;

    try {
      const points = pointAdjustment.points;

      await lineAxios.put(`/line/members/${pointAdjustment.member.line_id}/points`, {
        points,
        operation: pointAdjustment.operation
      });

      alert(`ポイントを${pointAdjustment.operation === 'add' ? '追加' : pointAdjustment.operation === 'subtract' ? '減算' : '設定'}しました`);
      await fetchMembers();
      setPointAdjustment({ isOpen: false, member: null, points: 0, operation: 'add' });
    } catch (error) {
      console.error('ポイント調整エラー:', error);
      alert('ポイント調整に失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PageContainer><div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">LINE会員管理</h1>
            <p className="text-gray-600">会員情報・ラベル・メモ管理</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/line/statistics')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <BarChart className="w-5 h-5" />
              統計ダッシュボード
            </button>
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
            {dynamicRanks.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Settings className="w-4 h-4" />
                <span>ランク数: {dynamicRanks.length}段階</span>
              </div>
            )}
          </div>
        </div>
        
        {/* 管理メニューボタン */}
        <div className="flex gap-3">
          <button
            onClick={() => setShowManagementModal('coupon')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all shadow-md"
          >
            <Ticket className="w-5 h-5" />
            <span>クーポン管理</span>
          </button>
          <button
            onClick={() => setShowManagementModal('message')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md"
          >
            <FileText className="w-5 h-5" />
            <span>メッセージ管理</span>
          </button>
          <button
            onClick={() => setShowLabelManagement(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-md"
          >
            <Tag className="w-5 h-5" />
            <span>ラベル管理</span>
          </button>
        </div>
      </div>

      {/* 統計カード */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <User className="w-5 h-5 text-blue-600" />
              <span className="text-xs text-gray-500">総会員数</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.total_members}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Trophy className="w-5 h-5 text-yellow-600" />
              <span className="text-xs text-gray-500">総ポイント</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.total_points.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-green-600" />
              <span className="text-xs text-gray-500">アクティブ(30日)</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.active_members_30days}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              <span className="text-xs text-gray-500">平均購入額</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{formatCurrency(stats.avg_purchase_amount)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              <span className="text-xs text-gray-500">平均来店</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.avg_visit_count.toFixed(1)}回</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-indigo-600" />
              <span className="text-xs text-gray-500">選択中</span>
            </div>
            <div className="text-2xl font-bold text-gray-800">{selectedMembers.size}名</div>
          </div>
        </div>
      )}

      {/* フィルター設定パネル */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">絞り込み条件</h3>
            <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* 動的ランクフィルター */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ランク（{dynamicRanks.length}段階）
              </label>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                {dynamicRanks.map(rank => (
                  <label key={rank.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={filters.rankFilter.includes(rank.id)}
                      onChange={(e) => {
                        const newRanks = e.target.checked
                          ? [...filters.rankFilter, rank.id]
                          : filters.rankFilter.filter(id => id !== rank.id);
                        setFilters({...filters, rankFilter: newRanks});
                      }}
                      className="mr-2"
                    />
                    <span 
                      className="text-sm flex items-center gap-1"
                      style={getRankStyle(rank.color, false)}
                    >
                      <span>{rank.icon}</span>
                      <span>{rank.rank_name}</span>
                      <span className="text-xs text-gray-500 ml-1">
                        ({rank.min_points}pt{rank.max_points ? `〜${rank.max_points}pt` : '〜'})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* ポイント範囲 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">現在ポイント</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="最小"
                  value={filters.pointRange.min}
                  onChange={(e) => setFilters({...filters, pointRange: {...filters.pointRange, min: parseInt(e.target.value) || 0}})}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
                <span className="self-center">〜</span>
                <input
                  type="number"
                  placeholder="最大"
                  value={filters.pointRange.max}
                  onChange={(e) => setFilters({...filters, pointRange: {...filters.pointRange, max: parseInt(e.target.value) || 9999999}})}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>

            {/* 来店回数範囲 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">来店回数</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="最小"
                  value={filters.visitRange.min}
                  onChange={(e) => setFilters({...filters, visitRange: {...filters.visitRange, min: parseInt(e.target.value) || 0}})}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
                <span className="self-center">〜</span>
                <input
                  type="number"
                  placeholder="最大"
                  value={filters.visitRange.max}
                  onChange={(e) => setFilters({...filters, visitRange: {...filters.visitRange, max: parseInt(e.target.value) || 999}})}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>

            {/* 累計購入額範囲 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">累計購入額</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="最小"
                  value={filters.purchaseRange.min}
                  onChange={(e) => setFilters({...filters, purchaseRange: {...filters.purchaseRange, min: parseInt(e.target.value) || 0}})}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
                <span className="self-center">〜</span>
                <input
                  type="number"
                  placeholder="最大"
                  value={filters.purchaseRange.max}
                  onChange={(e) => setFilters({...filters, purchaseRange: {...filters.purchaseRange, max: parseInt(e.target.value) || 99999999}})}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>

            {/* 最終来店 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">最終来店</label>
              <select
                value={filters.lastVisitDays || ''}
                onChange={(e) => setFilters({...filters, lastVisitDays: e.target.value ? parseInt(e.target.value) : null})}
                className="w-full px-2 py-1 border rounded text-sm"
              >
                <option value="">すべて</option>
                <option value="7">7日以内</option>
                <option value="30">30日以内</option>
                <option value="60">60日以内</option>
                <option value="90">90日以内</option>
                <option value="180">180日以内</option>
              </select>
            </div>

            {/* 会員歴 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">会員歴</label>
              <select
                value={filters.memberSince || ''}
                onChange={(e) => setFilters({...filters, memberSince: e.target.value ? parseInt(e.target.value) : null})}
                className="w-full px-2 py-1 border rounded text-sm"
              >
                <option value="">すべて</option>
                <option value="30">30日以上</option>
                <option value="90">90日以上</option>
                <option value="180">180日以上</option>
                <option value="365">1年以上</option>
                <option value="730">2年以上</option>
              </select>
            </div>

            {/* リセットボタン */}
            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  rankFilter: [],
                  pointRange: { min: 0, max: 9999999 },
                  visitRange: { min: 0, max: 999 },
                  purchaseRange: { min: 0, max: 99999999 },
                  lastVisitDays: null,
                  memberSince: null
                })}
                className="px-4 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
              >
                条件をリセット
              </button>
            </div>
          </div>

          {/* 選択中のランク表示 */}
          {filters.rankFilter.length > 0 && (
            <div className="mt-4 p-2 bg-blue-50 rounded">
              <span className="text-sm text-blue-700">
                選択中のランク: {
                  filters.rankFilter.map(id => {
                    const rank = dynamicRanks.find(r => r.id === id);
                    return rank ? `${rank.icon} ${rank.rank_name}` : '';
                  }).filter(Boolean).join(', ')
                }
              </span>
            </div>
          )}
        </div>
      )}

      {/* 一括アクションパネル */}
      {selectedMembers.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <span className="text-blue-800 font-medium">
                {selectedMembers.size}名を選択中
              </span>
              <button
                onClick={() => setSelectedMembers(new Set())}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                選択を解除
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  await fetchAvailableCoupons();
                  setShowCouponIssueModal(true);
                }}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 flex items-center gap-2"
              >
                <Ticket className="w-4 h-4" />
                クーポン発行
              </button>
              <button
                onClick={() => setShowMessageSender(true)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                メッセージ送信
              </button>
              <button
                onClick={() => setBulkActionModal({ isOpen: true, type: 'points' })}
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center gap-2"
              >
                <Gift className="w-4 h-4" />
                ポイント付与
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* 会員リスト */}
        <div className="flex-1 bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                会員一覧 ({filteredAndSortedMembers.length}名)
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-1 border rounded-md text-sm hover:bg-gray-50 flex items-center gap-1 ${showFilters ? 'bg-blue-50 border-blue-500' : ''}`}
                >
                  <Filter className="w-4 h-4" />
                  絞り込み
                  {filters.rankFilter.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                      {filters.rankFilter.length}
                    </span>
                  )}
                </button>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-1 border rounded-md text-sm"
                >
                  <option value="recent">最終来店順</option>
                  <option value="points">ポイント順</option>
                  <option value="visits">来店回数順</option>
                  <option value="purchase">累計購入額順</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-1 border rounded-md text-sm hover:bg-gray-50"
                >
                  {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="名前、LINE IDで検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                />
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="メモで検索..."
                    value={memoSearchKeyword}
                    onChange={(e) => setMemoSearchKeyword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-md"
                  />
                </div>
                <select
                  value={labelFilter || ''}
                  onChange={(e) => setLabelFilter(e.target.value ? parseInt(e.target.value) : null)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="">全ラベル</option>
                  {allLabels.map(label => (
                    <option key={label.id} value={label.id}>
                      {label.name || label.label_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center gap-2 hover:text-blue-600"
                    >
                      {selectedMembers.size === filteredAndSortedMembers.length && filteredAndSortedMembers.length > 0 ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      <span className="text-xs font-medium text-gray-500 uppercase">全選択</span>
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">会員</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ラベル</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ランク</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">ポイント</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">来店</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">累計購入</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedMembers.map((member) => {
                  const rankInfo = getDynamicRankInfo(member.total_points_earned || 0);
                  return (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleSelectMember(member.id)}
                          className="hover:text-blue-600"
                        >
                          {selectedMembers.has(member.id) ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            {/* ブロック中は名前を赤字表示 */}
                            <span className={`text-sm font-medium ${member.is_blocked === 1 ? 'text-red-600 font-bold' : 'text-gray-900'}`}>
                              {member.display_name || member.name || 'Unknown'}
                            </span>
                            {/* ブロック状態バッジ */}
                            {member.is_blocked === 1 && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold"
                                title={`ブロック中${member.blocked_at ? ' (' + new Date(member.blocked_at).toLocaleDateString('ja-JP') + ')' : ''}`}
                              >
                                ブロック中
                              </span>
                            )}
                            {/* 最新メッセージ既読/未読アイコン（ブロック中でない場合のみ） */}
                            {member.is_blocked !== 1 && member.latest_message_sent ? (
                              member.latest_message_read === 1 ? (
                                <span
                                  className="inline-flex items-center text-blue-600"
                                  title={`既読 (${member.latest_message_read_at ? new Date(member.latest_message_read_at).toLocaleString('ja-JP') : '日時不明'})`}
                                >
                                  <MailOpen className="w-3.5 h-3.5" />
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center text-gray-400"
                                  title={`未読 (送信: ${new Date(member.latest_message_sent).toLocaleString('ja-JP')})`}
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                </span>
                              )
                            ) : null}
                          </div>
                          <div className="text-xs text-gray-500">W{String(member.id).padStart(4, '0')}</div>
                          {memberExtensions.get(member.line_id)?.memo && (
                            <div className="text-xs text-gray-600 mt-1 italic">
                              📝 {memberExtensions.get(member.line_id)?.memo}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {memberExtensions.get(member.line_id)?.labels?.map((label, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: label.color }}
                            >
                              {label.name}
                            </span>
                          )) || <span className="text-xs text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {rankInfo.rank && (
                          <span 
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                            style={getRankStyle(rankInfo.rank.color, true)}
                          >
                            <span className="mr-1">{rankInfo.rank.icon}</span>
                            {rankInfo.rank.rank_name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm font-medium">{member.points || 0}pt</div>
                        <div className="text-xs text-gray-500">累計: {member.total_points_earned || 0}pt</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="text-sm">{(member.visits_count || member.visit_count || 0)}回</div>
                        <div className="text-xs text-gray-500">{formatVisitDate(member.last_visit)}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-sm font-medium">
                          {formatCurrency(member.total_purchase || member.total_purchases)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setEditingMemo({ lineId: member.line_id, memo: memberExtensions.get(member.line_id)?.memo || '' })}
                            className="text-gray-600 hover:text-gray-800 p-1"
                            title="メモ編集"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMemberSelect(member)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            詳細
                          </button>
                          <button
                            onClick={() => setShowLabelModal(member)}
                            className="text-purple-600 hover:text-purple-800 p-1"
                            title="ラベル管理"
                          >
                            <Tag className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* サイドバー：詳細情報 */}
        <div className="w-96 space-y-6">
          {/* ポイントランキング */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">🏆 ポイントランキング TOP5</h3>
            <div className="space-y-3">
              {ranking.slice(0, 5).map((member, index) => {
                const rankInfo = getDynamicRankInfo(member.total_points_earned || 0);
                return (
                  <div key={member.id} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className={`text-lg font-bold mr-3 ${
                        index === 0 ? 'text-yellow-500' :
                        index === 1 ? 'text-gray-400' :
                        index === 2 ? 'text-orange-600' :
                        'text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <div className="text-sm font-medium">{member.display_name || member.name}</div>
                        <div className="text-xs text-gray-500">{member.points || 0}pt</div>
                      </div>
                    </div>
                    {rankInfo.rank && (
                      <span className="text-xs">{rankInfo.rank.icon}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 選択された会員の詳細 */}
          <div className="bg-white rounded-lg shadow">
            {selectedMember ? (
              <>
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">会員詳細</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`px-3 py-1 text-sm rounded ${
                        activeTab === 'overview' ? 'bg-blue-600 text-white' : 'bg-gray-100'
                      }`}
                    >
                      概要
                    </button>
                    <button
                      onClick={() => setActiveTab('pattern')}
                      className={`px-3 py-1 text-sm rounded ${
                        activeTab === 'pattern' ? 'bg-blue-600 text-white' : 'bg-gray-100'
                      }`}
                    >
                      分析
                    </button>
                    <button
                      onClick={() => setActiveTab('mypage')}
                      className={`px-3 py-1 text-sm rounded ${
                        activeTab === 'mypage' ? 'bg-blue-600 text-white' : 'bg-gray-100'
                      }`}
                    >
                      ⚙️ マイページ設定
                    </button>
                  </div>
                </div>
              </div>

              {activeTab === 'overview' && (
                <div className="p-4 space-y-4">
                  {/* ポイントカード情報 */}
                  {(() => {
                    const memberRankInfo = getDynamicRankInfo(selectedMember.total_points_earned || 0);
                    return (
                      <div className="bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="text-xs opacity-90">会員番号</div>
                            <div className="text-lg font-bold">W{String(selectedMember.id).padStart(4, '0')}</div>
                          </div>
                          <div className="text-right">
                            {memberRankInfo.rank && (
                              <div className="flex items-center">
                                <span className="text-2xl mr-2">{memberRankInfo.rank.icon}</span>
                                <div>
                                  <div className="text-sm font-bold">{memberRankInfo.rank.rank_name}</div>
                                  {memberRankInfo.pointsToNext > 0 && (
                                    <div className="text-xs opacity-90">
                                      次まで: {memberRankInfo.pointsToNext}pt
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-center my-4">
                          <div className="text-3xl font-bold">{selectedMember.points || 0} pt</div>
                          <div className="text-xs opacity-90">現在のポイント</div>
                        </div>
                        {/* プログレスバー */}
                        {memberRankInfo.nextRank && memberRankInfo.pointsToNext > 0 && (
                          <div className="mt-3">
                            <div className="w-full h-2 bg-white/30 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-white transition-all duration-300"
                                style={{ 
                                  width: `${Math.min(
                                    ((selectedMember.total_points_earned || 0) - (memberRankInfo.rank?.min_points || 0)) / 
                                    ((memberRankInfo.nextRank.min_points || 0) - (memberRankInfo.rank?.min_points || 0)) * 100,
                                    100
                                  )}%` 
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* 基本情報 */}
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-gray-600">会員名</div>
                      <div className="text-lg font-medium">{selectedMember.display_name || selectedMember.name}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">累計獲得</div>
                        <div className="text-xl font-bold text-blue-600">
                          {selectedMember.total_points_earned || 0}pt
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">来店回数</div>
                        <div className="text-xl font-bold text-green-600">
                          {(selectedMember.visits_count || selectedMember.visit_count || 0)}回
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">総購入額</div>
                      <div className="text-lg font-medium">
                        {formatCurrency(selectedMember.total_purchase || selectedMember.total_purchases)}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">最終来店</div>
                        <div className="text-sm">{formatVisitDate(selectedMember.last_visit)}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">登録日</div>
                        <div className="text-sm">{formatDate(selectedMember.created_at)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t">
                    <button
                      onClick={() => setPointAdjustment({
                        isOpen: true,
                        member: selectedMember,
                        points: 0,
                        operation: 'add'
                      })}
                      className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      ポイント調整
                    </button>
                  </div>
                </div>
              )}

              {/* 分析タブ（来店パターン） */}
              {activeTab === 'pattern' && (
                <div className="p-4 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-3">📊 来店パターン分析</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center py-2 border-b border-blue-100">
                        <span className="text-gray-700">総来店回数</span>
                        <span className="font-semibold text-blue-900">{selectedMember.visit_count || 0}回</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-blue-100">
                        <span className="text-gray-700">平均購入金額</span>
                        <span className="font-semibold text-blue-900">
                          {selectedMember.total_purchase && selectedMember.visit_count
                            ? `¥${Math.round(selectedMember.total_purchase / selectedMember.visit_count).toLocaleString()}`
                            : '¥0'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-blue-100">
                        <span className="text-gray-700">累計購入金額</span>
                        <span className="font-semibold text-blue-900">¥{(selectedMember.total_purchase || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700">最終来店日</span>
                        <span className="font-semibold text-blue-900">
                          {formatVisitDate(selectedMember.last_visit)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      💡 詳細な来店パターン分析（曜日別・時間帯別）は今後のアップデートで追加予定です。
                    </p>
                  </div>
                </div>
              )}

              {/* マイページ設定タブ */}
              {activeTab === 'mypage' && (
                <MemberProfileSettings lineUserId={selectedMember.line_id} />
              )}
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>会員を選択してください</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* 一括アクションモーダル（既存のコード） */}
      {bulkActionModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">
              {bulkActionModal.type === 'coupon' && 'クーポン発行'}
              {bulkActionModal.type === 'message' && 'メッセージ送信'}
              {bulkActionModal.type === 'points' && 'ポイント付与'}
            </h3>
            
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-2">
                対象: {selectedMembers.size}名の会員
              </div>
            </div>

            {/* ポイント付与用UI */}
            {bulkActionModal.type === 'points' && (
              <div className="space-y-4">
                {/* 操作タイプ選択 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">操作</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setBulkPointAdjustment({...bulkPointAdjustment, operation: 'add'})}
                      className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        bulkPointAdjustment.operation === 'add'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      追加
                    </button>
                    <button
                      onClick={() => setBulkPointAdjustment({...bulkPointAdjustment, operation: 'subtract'})}
                      className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        bulkPointAdjustment.operation === 'subtract'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      減算
                    </button>
                    <button
                      onClick={() => setBulkPointAdjustment({...bulkPointAdjustment, operation: 'set'})}
                      className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        bulkPointAdjustment.operation === 'set'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      設定
                    </button>
                  </div>
                </div>

                {/* ポイント数入力 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {bulkPointAdjustment.operation === 'set' ? '設定するポイント数' : 'ポイント数'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={bulkPointAdjustment.points}
                    onChange={(e) => setBulkPointAdjustment({...bulkPointAdjustment, points: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ポイント数を入力"
                  />
                </div>

                {/* プレビュー */}
                <div className="p-3 bg-gray-50 rounded-md">
                  <div className="text-sm text-gray-600 mb-1">処理内容:</div>
                  <div className="text-lg font-bold text-purple-600">
                    {selectedMembers.size}名に{bulkPointAdjustment.points}ptを
                    {bulkPointAdjustment.operation === 'add' ? '追加' :
                     bulkPointAdjustment.operation === 'subtract' ? '減算' : '設定'}
                    します
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setBulkActionModal({ isOpen: false, type: null });
                  setBulkPointAdjustment({ points: 0, operation: 'add' });
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                キャンセル
              </button>
              <button
                onClick={handleBulkAction}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                実行
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* クーポン発行モーダル */}
      {showCouponIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="border-b p-4 bg-gradient-to-r from-orange-500 to-orange-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-6 h-6" />
                クーポン発行 - {selectedMembers.size}名に発行
              </h2>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {availableCoupons.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Ticket className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="mb-4">利用可能なクーポンがありません</p>
                  <button
                    onClick={() => {
                      setShowCouponIssueModal(false);
                      setShowManagementModal('coupon');
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    クーポンを作成
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* 既存クーポンの扱い（一番上に常に表示） */}
                  <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      ⚠️ 既存クーポンの扱い：
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="existingCouponHandling"
                          checked={!skipExistingCheck}
                          onChange={() => setSkipExistingCheck(false)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-sm">スキップする（推奨）</div>
                          <div className="text-xs text-gray-600">
                            既にこのクーポンを持っている会員には発行しません。重複を防ぎます。
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="existingCouponHandling"
                          checked={skipExistingCheck}
                          onChange={() => setSkipExistingCheck(true)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-sm text-orange-600">強制発行する</div>
                          <div className="text-xs text-gray-600">
                            既に持っている会員にも新しいクーポンを発行します。複数所持可能になります。
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* 発行内容の確認（クーポン選択後、黄色い枠のすぐ下に表示） */}
                  {selectedCouponForIssue && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold mb-2">✓ 発行内容の確認：</h4>
                      <ul className="space-y-1 text-sm">
                        <li>• クーポン: {selectedCouponForIssue.name}</li>
                        <li>• 対象者: {selectedMembers.size}名</li>
                        <li>• 有効期限: 発行日から{selectedCouponForIssue.valid_days || 30}日間</li>
                        <li>• 使用回数制限: {selectedCouponForIssue.usage_limit}回</li>
                      </ul>
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="font-semibold mb-2">発行するクーポンを選択：</h3>
                  </div>

                  <div className="grid gap-3">
                    {availableCoupons.map((coupon) => (
                      <div
                        key={coupon.id}
                        onClick={() => setSelectedCouponForIssue(coupon)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedCouponForIssue?.id === coupon.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-2xl">{coupon.icon}</span>
                              <h4 className="font-semibold">{coupon.name}</h4>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{coupon.description}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-500">
                                コード: <span className="font-mono">{coupon.code}</span>
                              </span>
                              {coupon.type === 'amount' ? (
                                <span className="text-orange-600 font-semibold">
                                  {coupon.value}円割引
                                </span>
                              ) : (
                                <span className="text-orange-600 font-semibold">
                                  {coupon.value}%割引
                                </span>
                              )}
                              {coupon.min_purchase > 0 && (
                                <span className="text-gray-500">
                                  最低購入額: {coupon.min_purchase}円
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedCouponForIssue?.id === coupon.id && (
                            <div className="text-orange-500">
                              <Check className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t p-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCouponIssueModal(false);
                  setSelectedCouponForIssue(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                キャンセル
              </button>
              {selectedCouponForIssue && (
                <button
                  onClick={handleIssueCoupon}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  発行する
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PIN発行結果モーダル */}
      {showPinResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="border-b p-4 flex justify-between items-center bg-gradient-to-r from-orange-500 to-red-500">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-6 h-6" />
                クーポンPIN発行完了
              </h2>
              <button
                onClick={() => {
                  setShowPinResultModal(false);
                  setIssuedPins([]);
                }}
                className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="mb-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>発行されたPIN番号一覧</strong>
                </p>
                <p className="text-xs text-gray-600">
                  お客様にはPIN番号をお伝えください。店舗でPIN番号を入力することでクーポンが適用されます。
                </p>
              </div>

              <div className="space-y-3">
                {issuedPins.map((result, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-800">{result.memberName}</p>
                        <p className="text-xs text-gray-500">{result.tier}会員</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 mb-1">PIN番号</p>
                        <p className="text-2xl font-bold text-orange-600 tracking-wider">
                          {result.pin}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>PIN番号について：</strong><br/>
                  • 2桁: プレミアム会員（上位99位）<br/>
                  • 3桁: レギュラー会員（100-999位）<br/>
                  • 4桁: スタンダード会員（1000位以降）
                </p>
              </div>
            </div>
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => {
                  setShowPinResultModal(false);
                  setIssuedPins([]);
                }}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* クーポン発行結果モーダル */}
      {showIssueResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b p-4 flex justify-between items-center bg-gradient-to-r from-green-500 to-green-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-6 h-6" />
                クーポン発行結果
              </h2>
              <button
                onClick={() => {
                  setShowIssueResultModal(false);
                  if (issuedPins.length > 0) {
                    setShowPinResultModal(true);
                  }
                }}
                className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {/* サマリー */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{couponIssueResults.success.length}</p>
                    <p className="text-sm text-gray-600 mt-1">✅ 発行成功</p>
                  </div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-yellow-600">{couponIssueResults.skipped.length}</p>
                    <p className="text-sm text-gray-600 mt-1">⏭️ スキップ</p>
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-red-600">{couponIssueResults.failed.length}</p>
                    <p className="text-sm text-gray-600 mt-1">❌ 発行失敗</p>
                  </div>
                </div>
              </div>

              {/* 成功リスト */}
              {couponIssueResults.success.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    発行成功 ({couponIssueResults.success.length}名)
                  </h3>
                  <div className="bg-green-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {couponIssueResults.success.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="font-medium">{item.name}</span>
                          <span className="text-gray-500 text-xs">({item.lineId})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* スキップリスト */}
              {couponIssueResults.skipped.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    スキップ ({couponIssueResults.skipped.length}名)
                  </h3>
                  <div className="bg-yellow-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {couponIssueResults.skipped.map((item, index) => (
                        <div key={index} className="text-sm">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <span className="font-medium">{item.name}</span>
                              <span className="text-gray-500 text-xs ml-2">({item.lineId})</span>
                              <p className="text-xs text-gray-600 mt-1">{item.reason}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 失敗リスト */}
              {couponIssueResults.failed.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <X className="w-5 h-5" />
                    発行失敗 ({couponIssueResults.failed.length}名)
                  </h3>
                  <div className="bg-red-50 rounded-lg p-4 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {couponIssueResults.failed.map((item, index) => (
                        <div key={index} className="text-sm">
                          <div className="flex items-start gap-2">
                            <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <span className="font-medium">{item.name}</span>
                              <span className="text-gray-500 text-xs ml-2">({item.lineId})</span>
                              <p className="text-xs text-red-600 mt-1">{item.reason}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 補足情報 */}
              {couponIssueResults.skipped.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-700">
                    <strong>💡 ヒント：</strong> スキップされた会員は既にこのクーポンを所持しています。
                    再度発行したい場合は、「強制発行する」オプションを選択してください。
                  </p>
                </div>
              )}
            </div>

            <div className="border-t p-4 bg-gray-50 flex gap-2">
              <button
                onClick={() => {
                  setShowIssueResultModal(false);
                  if (issuedPins.length > 0) {
                    setShowPinResultModal(true);
                  }
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {issuedPins.length > 0 ? 'PIN番号を確認' : '閉じる'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* クーポン管理モーダル */}
      {showManagementModal === 'coupon' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b p-4 flex justify-between items-center bg-gradient-to-r from-purple-500 to-purple-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Ticket className="w-6 h-6" />
                クーポン管理
              </h2>
              <button
                onClick={() => setShowManagementModal(null)}
                className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <CouponManagement />
            </div>
          </div>
        </div>
      )}

      {/* メッセージ管理モーダル */}
      {showManagementModal === 'message' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b p-4 flex justify-between items-center bg-gradient-to-r from-blue-500 to-blue-600">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FileText className="w-6 h-6" />
                メッセージ管理
              </h2>
              <button
                onClick={() => setShowManagementModal(null)}
                className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <MessageTemplates />
            </div>
          </div>
        </div>
      )}

      {/* ラベル管理モーダル */}
      {showLabelManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">ラベル管理</h2>
              <button
                onClick={() => setShowLabelManagement(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {/* 新規ラベル作成 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">新規ラベル作成</h3>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="ラベル名"
                    value={newLabel.name}
                    onChange={(e) => setNewLabel({ ...newLabel, name: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-md"
                  />
                  <input
                    type="color"
                    value={newLabel.color}
                    onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })}
                    className="w-20 h-10 border rounded-md cursor-pointer"
                  />
                  <button
                    onClick={createLabel}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* 既存ラベル一覧 */}
              <div>
                <h3 className="text-lg font-semibold mb-3">既存ラベル</h3>
                <div className="space-y-2">
                  {allLabels.map(label => (
                    <div key={label.id} className="flex items-center justify-between p-3 border rounded-md">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full"
                          style={{ backgroundColor: label.color }}
                        />
                        <span className="font-medium">{label.name || label.label_name}</span>
                      </div>
                      <button
                        onClick={async () => {
                          if (confirm(`「${label.name || label.label_name}」を削除しますか？`)) {
                            try {
                              await lineAxios.delete(`/member-labels/labels/${label.id}`);
                              await fetchLabels();
                              await fetchMemberExtensions();
                            } catch (error) {
                              console.error('ラベル削除エラー:', error);
                              alert('ラベルの削除に失敗しました');
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* メモ編集モーダル */}
      {editingMemo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">メモ編集</h2>
              <button
                onClick={() => setEditingMemo(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea
                value={editingMemo.memo}
                onChange={(e) => setEditingMemo({ ...editingMemo, memo: e.target.value })}
                className="w-full h-32 px-3 py-2 border rounded-md resize-none"
                placeholder="メモを入力..."
              />
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setEditingMemo(null)}
                  className="px-4 py-2 border rounded-md hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  onClick={async () => {
                    await saveMemo(editingMemo.lineId, editingMemo.memo);
                    setEditingMemo(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 会員詳細でのラベル割り当て */}
      {showLabelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">{showLabelModal.display_name || showLabelModal.name} - ラベル管理</h2>
              <button
                onClick={() => setShowLabelModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-3">ラベル割り当て</h3>
                <div className="space-y-2">
                  {allLabels.map(label => {
                    const isAssigned = memberExtensions.get(showLabelModal.line_id)?.labels?.some(l => l.name === label.name);
                    return (
                      <label key={label.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAssigned || false}
                          onChange={async (e) => {
                            if (e.target.checked) {
                              await assignLabel(showLabelModal.line_id, label.id);
                            } else {
                              const assignedLabel = memberExtensions.get(showLabelModal.line_id)?.labels?.find(l => l.name === label.name);
                              if (assignedLabel && assignedLabel.id) {
                                await removeLabel(showLabelModal.line_id, label.id);
                              }
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <div
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: label.color }}
                        />
                        <span>{label.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* メッセージ送信モーダル */}
      {showMessageSender && (
        <MessageSender
          selectedMembers={members.filter(m => selectedMembers.has(m.id)).map(m => ({
            id: m.id,
            line_user_id: m.line_id,
            display_name: m.display_name,
            phone: '',
            is_blocked: false
          }))}
          onClose={() => setShowMessageSender(false)}
        />
      )}

      {/* ポイント調整モーダル */}
      {pointAdjustment.isOpen && pointAdjustment.member && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4">
              <h2 className="text-xl font-bold">ポイント調整</h2>
              <p className="text-sm text-gray-600 mt-1">
                {pointAdjustment.member.display_name || pointAdjustment.member.name} (現在: {pointAdjustment.member.points || 0}pt)
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* 操作タイプ選択 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">操作</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPointAdjustment({...pointAdjustment, operation: 'add'})}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                      pointAdjustment.operation === 'add'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    追加
                  </button>
                  <button
                    onClick={() => setPointAdjustment({...pointAdjustment, operation: 'subtract'})}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                      pointAdjustment.operation === 'subtract'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    減算
                  </button>
                  <button
                    onClick={() => setPointAdjustment({...pointAdjustment, operation: 'set'})}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                      pointAdjustment.operation === 'set'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    設定
                  </button>
                </div>
              </div>

              {/* ポイント数入力 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {pointAdjustment.operation === 'set' ? '設定するポイント数' : 'ポイント数'}
                </label>
                <input
                  type="number"
                  min="0"
                  value={pointAdjustment.points}
                  onChange={(e) => setPointAdjustment({...pointAdjustment, points: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ポイント数を入力"
                />
              </div>

              {/* プレビュー */}
              <div className="p-3 bg-gray-50 rounded-md">
                <div className="text-sm text-gray-600 mb-1">変更後のポイント:</div>
                <div className="text-2xl font-bold text-blue-600">
                  {pointAdjustment.operation === 'add'
                    ? (pointAdjustment.member.points || 0) + pointAdjustment.points
                    : pointAdjustment.operation === 'subtract'
                    ? Math.max(0, (pointAdjustment.member.points || 0) - pointAdjustment.points)
                    : pointAdjustment.points
                  } pt
                </div>
              </div>
            </div>

            <div className="border-t p-4 flex justify-end gap-3">
              <button
                onClick={() => setPointAdjustment({ isOpen: false, member: null, points: 0, operation: 'add' })}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={handlePointAdjustment}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                確定
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default LineMemberManagementDynamic;
